import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Select, Button, InputNumber, Row, Col, Space, Divider, Tabs, Spin } from 'antd';
import { toast } from 'react-toastify';
import { cloneDeep, set } from 'lodash';

import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { IoMdInformationCircle, IoMdPricetags } from "react-icons/io";
import { FaRegStickyNote, FaMinusCircle, FaPlus, FaCalculator } from "react-icons/fa";

import '../../../css/UserAdmin/Global.css'; 
import 'react-toastify/dist/ReactToastify.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const getNum = (val) => Number(val) || 0;

// Reusable props for all our numeric inputs to ensure consistency
const defaultInputNumberProps = {
    min: 0,
    style: { width: '100%' },
    // This parser strips any non-numeric characters, just in case.
    parser: (value) => value.replace(/[^\d.]/g, ''),
};

// Comprehensive initial values for "create" mode to pre-fill all numbers with 0
const initialFormValues = {
    fees: {
        tuition: { baseAmount: 0, finalAmount: 0 },
        miscellaneous: { registration: 0, medicalDental: 0, library: 0, guidance: 0, instructionalMedia: 0, athletic: 0, total: 0, finalTotal: 0 },
        otherFees: {
            energyFee: { baseAmount: 0, finalAmount: 0 },
            studentDevelopment: 0, testing: 0, schoolID: 0, recollection: 0, internet: 0,
            labFees: { generalChemistry1: 0, generalBiology1: 0, specializedSubjects: 0 },
            total: 0, finalTotal: 0
        },
        baseTotal: 0, finalTotal: 0, discount: 0,
        installment: { breakdown: [] }
    }
};

const CreateTuitionDetails = () => {
    const navigate = useNavigate();
    const { id, mode } = useParams();
    const [form] = Form.useForm();
    
    const [loading, setLoading] = useState(false);
    const [isViewMode, setIsViewMode] = useState(mode === 'view');
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);

    const paymentTypeValue = Form.useWatch('paymentType', form);
    const feesValues = Form.useWatch('fees', form);

    useEffect(() => {
        const fetchTuitionData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tuition-details/${id}`);
                const result = await response.json();
                if (response.ok) {
                    form.setFieldsValue(result);
                    if (result.isArchived) setIsViewMode(true);
                } else {
                    toast.error(result.message || 'Failed to load data.');
                    navigate('/admin/manage-tuition');
                }
            } catch (error) {
                toast.error('Error fetching data: ' + error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTuitionData();
    }, [id, form, navigate]);

    // This useEffect is for VISUAL feedback to the user
    useEffect(() => {
        if (!feesValues || isViewMode) return;
        const { tuition = {}, miscellaneous = {}, otherFees = {}, discount = 0 } = feesValues;
        const { labFees = {} } = otherFees;
        const miscTotal = getNum(miscellaneous.registration) + getNum(miscellaneous.medicalDental) + getNum(miscellaneous.library) + getNum(miscellaneous.guidance) + getNum(miscellaneous.instructionalMedia) + getNum(miscellaneous.athletic);
        const labTotal = getNum(labFees.generalChemistry1) + getNum(labFees.generalBiology1) + getNum(labFees.specializedSubjects);
        const otherTotal = getNum(otherFees.energyFee?.baseAmount) + getNum(otherFees.studentDevelopment) + getNum(otherFees.testing) + getNum(otherFees.schoolID) + getNum(otherFees.recollection) + getNum(otherFees.internet) + labTotal;
        const baseTotal = getNum(tuition.baseAmount) + miscTotal + otherTotal;
        
        form.setFieldsValue({
            fees: { ...feesValues, miscellaneous: { ...miscellaneous, total: miscTotal }, otherFees: { ...otherFees, total: otherTotal }, baseTotal, finalTotal: baseTotal - getNum(discount) }
        });
    }, [feesValues, form, isViewMode]);

    const handleSubmit = async (values) => {
        if (isViewMode) return;
        setLoading(true);

        const payload = cloneDeep(values);

        // Safely access nested properties
        const fees = payload.fees || {};
        const tuition = fees.tuition || {};
        const miscellaneous = fees.miscellaneous || {};
        const otherFees = fees.otherFees || {};
        const energyFee = otherFees.energyFee || {};
        const labFees = otherFees.labFees || {};

        // 1. Calculate totals from the breakdown fields
        const miscTotal = getNum(miscellaneous.registration) + getNum(miscellaneous.medicalDental) + getNum(miscellaneous.library) + getNum(miscellaneous.guidance) + getNum(miscellaneous.instructionalMedia) + getNum(miscellaneous.athletic);
        const labTotal = getNum(labFees.generalChemistry1) + getNum(labFees.generalBiology1) + getNum(labFees.specializedSubjects);
        const otherTotal = getNum(energyFee.baseAmount) + getNum(otherFees.studentDevelopment) + getNum(otherFees.testing) + getNum(otherFees.schoolID) + getNum(otherFees.recollection) + getNum(otherFees.internet) + labTotal;
        const baseTotal = getNum(tuition.baseAmount) + miscTotal + otherTotal;
        const finalPayable = baseTotal - getNum(fees.discount);

        // 2. Set ALL required fields on the payload object to ensure data integrity
        set(payload, 'fees.miscellaneous.total', miscTotal);
        set(payload, 'fees.miscellaneous.finalTotal', miscTotal);
        set(payload, 'fees.otherFees.total', otherTotal);
        set(payload, 'fees.otherFees.finalTotal', otherTotal);
        set(payload, 'fees.otherFees.energyFee.finalAmount', getNum(energyFee.baseAmount));
        set(payload, 'fees.baseTotal', baseTotal);
        set(payload, 'fees.finalTotal', finalPayable);

        // Ensure every required number field is actually a number, not null/undefined
        set(payload, 'fees.tuition.baseAmount', getNum(tuition.baseAmount));
        set(payload, 'fees.tuition.finalAmount', getNum(tuition.finalAmount));
        set(payload, 'fees.discount', getNum(fees.discount));
        // ... and so on for all required number fields
        
        try {
            const url = id ? `${process.env.REACT_APP_API_URL}/api/admin/tuition-details/${id}` : `${process.env.REACT_APP_API_URL}/api/admin/tuition-details`;
            const method = id ? 'PUT' : 'POST';
            
            const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const result = await response.json();
            
            if (!response.ok) {
                const errorMsg = result.message + (result.errors ? `: ${Object.keys(result.errors).join(', ')}` : '');
                throw new Error(errorMsg);
            }

            toast.success(result.message);
            navigate('/admin/manage-tuition');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };


    const pageTitle = isViewMode ? 'View Tuition Details' : (id ? 'Edit Tuition Details' : 'Create Tuition Details');
    
    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={() => setIsSideNavOpen(!isSideNavOpen)} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <SideNav isOpen={isSideNavOpen} onClose={() => setIsSideNavOpen(false)} isSmallScreen={window.innerWidth < 768} />
                <main className="admin-main-content-area">
                    <Spin spinning={loading}>
                        <div className="page-title">
                            <div className="arrows" onClick={() => navigate('/admin/manage-tuition-details')} role="button" tabIndex={0}><MdOutlineKeyboardArrowLeft /></div>
                            <p className="heading">{pageTitle}</p>
                        </div>
                        <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isViewMode} initialValues={initialFormValues}>
                             {/* BASIC INFO */}
                            <Row gutter={24}>
                                <Col xs={24} md={8}>
                                    <div className="form-section">
                                        <div className="group-title"><IoMdInformationCircle className="section-icon" /><p className="section-title">BASIC INFORMATION</p></div>
                                        <Form.Item label="Academic Year" name="academicYear" rules={[{ required: true, message: 'Academic Year is required' }, { pattern: /^AY \d{4}-\d{4}$/, message: 'Use format "AY YYYY-YYYY"' }]}><Input placeholder="e.g., AY 2024-2025" /></Form.Item>
                                        <Form.Item label="Category" name="category" rules={[{ required: true, message: 'Category is required' }]}><Select placeholder="Select..."><Option value="Public School">Public School</Option><Option value="Private with PEAC Voucher">Private (PEAC)</Option><Option value="Private without Voucher">Private (No Voucher)</Option></Select></Form.Item>
                                        <Form.Item label="Semester" name="semester" rules={[{ required: true, message: 'Semester is required' }]}><Select placeholder="Select..."><Option value="1st Semester">1st Semester</Option><Option value="2nd Semester">2nd Semester</Option></Select></Form.Item>
                                        <Form.Item label="Payment Type" name="paymentType" rules={[{ required: true, message: 'Payment type is required' }]}><Select placeholder="Select..."><Option value="Full Payment">Full Payment</Option><Option value="Installment">Installment</Option></Select></Form.Item>
                                    </div>
                                </Col>
                                <Col xs={24} md={16}>
                                    <div className="form-section">
                                        <div className="group-title"><IoMdPricetags className="section-icon" /><p className="section-title">FEE BREAKDOWN</p></div>
                                        <Tabs defaultActiveKey="1" type="card">
                                            <TabPane tab="Tuition & Misc" key="1">
                                                <Row gutter={16}>
                                                    <Col span={12}><Form.Item label="Tuition (Base)" name={['fees', 'tuition', 'baseAmount']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} addonAfter="PHP" /></Form.Item></Col>
                                                    <Col span={12}><Form.Item label="Tuition (Final)" name={['fees', 'tuition', 'finalAmount']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} addonAfter="PHP" /></Form.Item></Col>
                                                </Row>
                                                <Divider>Miscellaneous</Divider>
                                                <Row gutter={16}>
                                                    <Col span={8}><Form.Item label="Registration" name={['fees', 'miscellaneous', 'registration']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Medical/Dental" name={['fees', 'miscellaneous', 'medicalDental']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Library" name={['fees', 'miscellaneous', 'library']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Guidance" name={['fees', 'miscellaneous', 'guidance']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Instructional Media" name={['fees', 'miscellaneous', 'instructionalMedia']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Athletic" name={['fees', 'miscellaneous', 'athletic']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                </Row>
                                            </TabPane>
                                            <TabPane tab="Other & Lab" key="2">
                                                <Row gutter={16}>
                                                    <Col span={8}><Form.Item label="Energy Fee" name={['fees', 'otherFees', 'energyFee', 'baseAmount']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Student Dev" name={['fees', 'otherFees', 'studentDevelopment']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Testing" name={['fees', 'otherFees', 'testing']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="School ID" name={['fees', 'otherFees', 'schoolID']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Recollection" name={['fees', 'otherFees', 'recollection']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Internet" name={['fees', 'otherFees', 'internet']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                </Row>
                                                <Divider>Lab Fees</Divider>
                                                <Row gutter={16}>
                                                    <Col span={8}><Form.Item label="Gen. Chem 1" name={['fees', 'otherFees', 'labFees', 'generalChemistry1']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Gen. Bio 1" name={['fees', 'otherFees', 'labFees', 'generalBiology1']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                    <Col span={8}><Form.Item label="Specialized" name={['fees', 'otherFees', 'labFees', 'specializedSubjects']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} /></Form.Item></Col>
                                                </Row>
                                            </TabPane>
                                        </Tabs>
                                    </div>
                                </Col>
                            </Row>
                             {/* TOTALS and INSTALLMENT */}
                            <div className="form-section">
                                <div className="group-title"><FaCalculator className="section-icon" /><p className="section-title">TOTALS</p></div>
                                <Row gutter={16}>
                                    <Col span={6}><Form.Item label="Misc. Total" name={['fees', 'miscellaneous', 'total']}><InputNumber {...defaultInputNumberProps} readOnly /></Form.Item></Col>
                                    <Col span={6}><Form.Item label="Other Fees Total" name={['fees', 'otherFees', 'total']}><InputNumber {...defaultInputNumberProps} readOnly /></Form.Item></Col>
                                    <Col span={6}><Form.Item label="Base Total" name={['fees', 'baseTotal']}><InputNumber {...defaultInputNumberProps} readOnly /></Form.Item></Col>
                                    <Col span={6}><Form.Item label="Discount" name={['fees', 'discount']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} addonAfter="PHP" /></Form.Item></Col>
                                    <Col span={24}><Form.Item label="FINAL PAYABLE AMOUNT" name={['fees', 'finalTotal']} rules={[{ required: true }]}><InputNumber {...defaultInputNumberProps} style={{...defaultInputNumberProps.style, fontWeight: 'bold', fontSize: '1.2rem'}} addonAfter="PHP" readOnly /></Form.Item></Col>
                                </Row>
                            </div>
                            {paymentTypeValue === 'Installment' && (
                                <div className="form-section">
                                    <div className="group-title"><FaRegStickyNote className="section-icon" /><p className="section-title">INSTALLMENT PLAN</p></div>
                                    <Form.List name={['fees', 'installment', 'breakdown']}>
                                        {(fields, { add, remove }) => (<>{fields.map(({ key, name, ...rest }) => (
                                            <Space key={key} align="baseline"><Form.Item {...rest} name={[name, 'type']} rules={[{ required: true, message: 'Type is required' }]}><Select placeholder="Type" style={{ width: 150 }}><Option value="Downpayment">Downpayment</Option><Option value="Midterm">Midterm</Option><Option value="Final">Final</Option></Select></Form.Item><Form.Item {...rest} name={[name, 'amount']} rules={[{ required: true, message: 'Amount is required' }]}><InputNumber {...defaultInputNumberProps} placeholder="Amount" /></Form.Item><FaMinusCircle onClick={() => remove(name)} /></Space>
                                        ))}<Button type="dashed" onClick={() => add()} block icon={<FaPlus />}>Add Payment</Button></>)}
                                    </Form.List>
                                </div>
                            )}
                            <Form.Item label="Notes" name="notes"><TextArea rows={3} /></Form.Item>
                            <Form.Item className="form-actions"><Button type="default" onClick={() => navigate('/admin/manage-tuition')} style={{ marginRight: 8 }}>{isViewMode ? 'Back' : 'Cancel'}</Button>{!isViewMode && <Button type="primary" htmlType="submit" loading={loading} className={!id ? 'create-btn' : ''}>{id ? 'Update Record' : 'Save Record'}</Button>}</Form.Item>
                        </Form>
                    </Spin>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default CreateTuitionDetails;